// @file Composable to manage surface navbar visibility state, specifically for AI chat visibility
import { isAppUsing } from '@@/bits/flip'
import { isRegistered } from '@@/bits/user_model'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { computed, onMounted } from 'vue'

export const useSurfaceNavbarVisibility = () => {
  const surfaceStore = useSurfaceStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()
  const surfaceAIChatStore = useSurfaceAIChatStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const demoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()

  const xAiChat = computed(() => {
    if (!surfacePermissionsStore.amIOwner) return false
    if (!demoPadletPanelStore.demoPadletPanelVisibility.showSurfaceAiChat) return false
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) return false

    // For users that specifically enabled surface chat or are native admins, we show it
    if (isAppUsing('surfaceAiChat') || surfaceAccountsStore.isNativeAdmin) {
      return true
    }

    if (!surfaceStore.isMagicWall) return false
    if (surfaceStore.magicWallType === 'learning_assessment_polls') return false

    // Else, we show it for library and backpack teachers.
    if (surfaceAccountsStore.isLibraryTeacherOrAbove || surfaceAccountsStore.isBackpackTeacherOrAbove) return true

    return false
  })

  const xSignup = computed(() => {
    return isAppUsing('signUpSurface') && !isRegistered(surfaceStore.user) && !surfaceStore.isSimplifiedView
  })

  const initializeAiChat = async () => {
    await surfaceAccountsStore.initialize()

    // Load data to show feedback button, remove wall options if first time
    await surfaceAIChatStore.loadMagicWallFirstOpen(xAiChat.value)
  }

  onMounted(async () => {
    await initializeAiChat()
  })

  return { xAiChat, xSignup }
}
